import React, { useCallback } from "react";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import {
  createProjectUserPayloadModel,
  deleteuserInProject,
  ProjectUser,
} from "../../../../../model/projectModel";
import moment from "moment";
import { FaRegTrashCan } from "react-icons/fa6";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import ReusableSearchInput from "../../../../../components/ReusableSearchInput";
import { useManageClientContext } from "../../../../../context/ManageClientContex";
import { toast } from "react-toastify";

interface ProjectFormProps {
  name: string;
  user_id: number | null;
  start_ts: Date | null;
  end_ts: Date | null;
  approver: number | null;
  location: string;
  shift_details: string;
  is_approver: string;
  is_billable: string;
  user_type: string;
  description: string;
}

const defaultValue: ProjectFormProps = {
  name: "",
  start_ts: null,
  user_id: null,
  end_ts: null,
  approver: null,
  location: "",
  shift_details: "",
  is_approver: "",
  is_billable: "",
  user_type: "",
  description: "",
};

const TeamForms: React.FC<{
  formType: string;
  userValue: ProjectUser | null;
  closeModals: () => void;
}> = ({ formType, userValue, closeModals }) => {
  // console.log(userValue, "userValue");
  const {
    memberOptionDetail,
    addProjectMember,
    projectDetail,
    deleteProjectMember,
    getProjectDetails,
  } = useManageClientContext();

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ProjectFormProps>({ mode: "all", defaultValues: defaultValue });

  React.useEffect(() => {
    reset(defaultValue);
    if (userValue && formType === "update") {
      setValue("name", userValue["Employee ldap"]);
      // setValue("start_ts", moment(userValue.start).isValid() ? moment(userValue.start).toDate() : null);
      // setValue("end_ts", moment(userValue.end).isValid() ? moment(userValue.end).toDate() : null);
      setValue(
        "start_ts",
        moment(userValue.start, "DD/MM/YYYY").isValid()
          ? moment(userValue.start, "DD/MM/YYYY").toDate()
          : null,
      );
      setValue(
        "end_ts",
        moment(userValue.end, "DD/MM/YYYY").isValid()
          ? moment(userValue.end, "DD/MM/YYYY").toDate()
          : null,
      );
      setValue("approver", userValue["Approver_iontime_id"]);
      setValue("location", userValue.location);
      setValue("shift_details", userValue.shift_details);
      setValue("is_approver", userValue.is_approver);
      setValue("is_billable", userValue.is_billable);
      setValue("description", userValue?.description ?? "");
    }
  }, [reset, formType, userValue, setValue]);

  const onSubmit: SubmitHandler<ProjectFormProps> = async (data) => {
    if (data && memberOptionDetail && projectDetail) {
      const payloadResponse: createProjectUserPayloadModel = {
        approver_id: Number(data.approver),
        is_enabled: true,
        projects_id: projectDetail.id,
        users_id:
          data.user_id ??
          Number(userValue?.["Employee id"]),
        start_ts: data.start_ts,
        end_ts: data.end_ts,
        isaltmanager: "No",
        is_billable: data.is_billable,
        location: data.location,
        shift_details: data.shift_details,
        is_approver: data.is_approver,
        description: data.description,
      };
      await addProjectMember(payloadResponse);
      closeModals();
    }
  };

  const deleteProjectMembers = useCallback(
    async (
      userID: number | null,
      projectID: number | undefined,
      approverID?: number,
    ) => {
      if (userID && projectID) {
        const payloadResponse: deleteuserInProject = {
          users_id: userID,
          projects_id: projectID,
          ...(approverID ? { approver_id: approverID } : {}),
        };
        toast.dismiss(); // Dismiss the current toast
        await deleteProjectMember(payloadResponse);
        getProjectDetails(projectID);
      }
    },
    [],
  );

  const cancelDelete = () => {
    toast.dismiss(); // Dismiss the current toast
  };

  const confirmDelete = (
    userId: number,
    ProjectID: number | undefined,
    approverId?: number,
  ) => {
    toast.info(
      <div>
        <p>Are you sure you want to delete this user?</p>
        <div className="mt-2">
          <button
            className="px-4 py-1  tex-sm bg-blue-600 text-white"
            onClick={() => deleteProjectMembers(userId, ProjectID, approverId)}
          >
            Yes
          </button>
          <button
            className="px-4 py-1 mx-2 tex-sm bg-red-500 text-white"
            onClick={cancelDelete}
          >
            No
          </button>
        </div>
      </div>,
      {
        autoClose: false,
        closeButton: false,
      },
    );
  };

  return (
    <form className="" onSubmit={handleSubmit(onSubmit)}>
      <div className="max-w-4xl grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-2">
        <div className="mb-2 ">
          <label
            htmlFor="projectName"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            Name <span className="text-red-400">*</span>
          </label>
          <Controller
            name="name"
            control={control}
            defaultValue=""
            rules={{ required: "Name is required" }}
            render={({ field }) => (
              <ReusableSearchInput
                {...field}
                value={field.value ?? ""}
                onChange={field.onChange}
                disabled={formType === "update" ? true : false}
                setOptionsData={(user) => {
                  setValue("user_id", user?.id ?? null);
                  setValue("name", user?.ldap_uid ?? "");
                }}
              />
            )}
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        <div className="mb-2 ">
          <label
            htmlFor="approver"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            Approver <span className="text-red-400">*</span>
          </label>
          <Controller
            name="approver"
            control={control}
            rules={{ required: "Approver is required" }}
            render={({ field }) => (
              <select
                value={field.value?.toString()}
                onChange={field.onChange}
                className="shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light"
              >
                <option selected>Select</option>
                {memberOptionDetail &&
                  memberOptionDetail?.approvers.length > 0 &&
                  memberOptionDetail?.approvers.map((item) => (
                    <option key={item.name} value={item.userid}>
                      {item.name}
                    </option>
                  ))}
              </select>
            )}
          />
          <p
            id="helper-text-explanation"
            className=" text-sm text-gray-500 dark:text-gray-400"
          >
            Members are from LDAP
          </p>
          {errors.approver && (
            <p className="text-red-500 text-xs mt-1">
              {errors.approver.message}
            </p>
          )}
        </div>

        <div className="mb-2">
          <label
            htmlFor="startDate"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            Start Date <span className="text-red-400">*</span>
          </label>
          <Controller
            name="start_ts"
            control={control}
            rules={{ required: "Start Date is required" }}
            render={({ field }) => (
              <CustomDatePicker
                onChange={field.onChange}
                selectedDate={
                  moment(field.value).isValid()
                    ? moment(field.value).toDate()
                    : null
                }
                minDate={
                  moment(projectDetail?.start_ts).isValid()
                    ? moment(projectDetail?.start_ts).toDate()
                    : new Date()
                }
                isClearable={true}
                dateFormat="dd/MM/yyyy"
              />
            )}
          />
          {errors.start_ts && (
            <p className="text-red-500 text-sm mt-1">
              {errors.start_ts.message}
            </p>
          )}
        </div>

        <div className="mb-2">
          <label
            htmlFor="endDate"
            className="block mb-2  text-sm font-medium text-gray-900 dark:text-white"
          >
            End Date
          </label>
          <Controller
            name="end_ts"
            control={control}
            render={({ field }) => (
              <CustomDatePicker
                onChange={field.onChange}
                selectedDate={
                  moment(field.value).isValid()
                    ? moment(field.value).toDate()
                    : null
                }
                minDate={
                  moment(watch("start_ts")).isValid()
                    ? moment(watch("start_ts")).toDate()
                    : new Date()
                }
                isClearable={true}
                dateFormat="dd/MM/yyyy"
              />
            )}
          />
          {errors.end_ts && (
            <p className="text-red-500 text-sm mt-1">{errors.end_ts.message}</p>
          )}
        </div>

        <div className="mb-2">
          <label
            htmlFor="billing"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            Billing <span className="text-red-400">*</span>
          </label>
          <Controller
            name="is_billable"
            control={control}
            defaultValue=""
            rules={{ required: "Billing is required" }}
            render={({ field }) => (
              <select
                value={field.value}
                onChange={field.onChange}
                className="shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light"
              >
                <option selected>Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            )}
          />
          {errors.is_billable && (
            <p className="text-red-500 text-sm mt-1">
              {errors.is_billable.message}
            </p>
          )}
        </div>

        <div className="mb-2 ">
          <label
            htmlFor="department"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            Location <span className="text-red-400">*</span>
          </label>
          <Controller
            name="location"
            control={control}
            defaultValue=""
            rules={{ required: "Location is required" }}
            render={({ field }) => (
              <select
                value={field.value}
                onChange={field.onChange}
                className="shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light"
              >
                <option selected>Select</option>
                {memberOptionDetail &&
                  memberOptionDetail?.locations.length > 0 &&
                  memberOptionDetail?.locations.map((item) => (
                    <option key={item.loc_id} value={item.location_name}>
                      {item.location_name}
                    </option>
                  ))}
              </select>
            )}
          />
          {errors.location && (
            <p className="text-red-500 text-sm mt-1">
              {errors.location.message}
            </p>
          )}
        </div>

        <div className="mb-2 ">
          <label
            htmlFor="department"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            Shift Details <span className="text-red-400">*</span>
          </label>
          <Controller
            name="shift_details"
            control={control}
            defaultValue=""
            rules={{ required: "Shift detail is required" }}
            render={({ field }) => (
              <select
                value={field.value ? field.value.trim() : ""}
                onChange={field.onChange}
                className="shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light"
              >
                <option selected>Select </option>
                {memberOptionDetail &&
                  memberOptionDetail?.shifts.length > 0 &&
                  memberOptionDetail?.shifts.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
              </select>
            )}
          />
          {errors.shift_details && (
            <p className="text-red-500 text-sm mt-1">
              {errors.shift_details.message}
            </p>
          )}
        </div>

        <div className="mb-2">
          <label
            htmlFor="billing"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            Is Approver <span className="text-red-400">*</span>
          </label>
          <Controller
            name="is_approver"
            control={control}
            defaultValue=""
            rules={{ required: "Is Approver is required" }}
            render={({ field }) => (
              <select
                value={field.value}
                onChange={field.onChange}
                className="shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light"
              >
                <option selected>Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            )}
          />
          {errors.is_approver && (
            <p className="text-red-500 text-sm mt-1">
              {errors.is_approver.message}
            </p>
          )}
        </div>

        <div className="mb-2 col-span-full">
          <label
            htmlFor="description"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            Commants
          </label>
          <Controller
            name="description"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <textarea
                {...field}
                rows={2}
                maxLength={200}
                id="comment"
                className="shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light"
              />
            )}
          />
        </div>
      </div>
      <div className="w-full flex justify-between align-middle">
        <div>
          {formType === "update" && (
            <button
              type="button"
              onClick={() =>
                confirmDelete(
                  Number(userValue?.["Employee_iontime_id"] ?? userValue?.["Employee id"]),
                  projectDetail?.id,
                  userValue?.Approver_iontime_id,
                )
              }
              className="text-white bg-red-700 hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm w-full sm:w-auto p-2.5 text-center dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-800"
            >
              <FaRegTrashCan size={20} />
            </button>
          )}
        </div>
        <div>
          <button
            type="submit"
            className="text-white bg-blue-700 mr-2 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
          >
            Save
          </button>
          <button
            type="button"
            onClick={closeModals}
            className="text-white bg-red-700 hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-800"
          >
            Close
          </button>
        </div>
      </div>
    </form>
  );
};

export default TeamForms;
